/**
 * Reaper key: buyReaperKey() cuts 30s off the round timer.
 * Price = 5 × key price × 24h / time remaining (rises as the clock runs down).
 * Split (GameCoreFacet): 55% pot, 35% dividends, 5% jackpot pool, 5% community.
 * Blocked by the contract at ≤ 60s left (REAPER_SAFE_ZONE); never pushes the clock below 60s.
 * Phase 1.1: also blocked while the round has no keys (EmptyRound()); getReaperPrice() reads 0 then.
 */
import { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { buyReaperKey, getReaperPrice, getFRAMEContract } from '../utils/contract';

const SAFE_ZONE = 60;
const fmt = (wei: bigint) => {
  const n = Number(ethers.formatEther(wei));
  for (const [v, s] of [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']] as [number, string][]) if (n >= v) return `${(n / v).toFixed(2)}${s}`;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

interface Props {
  account: string | null;
  timeRemaining: number;
  allowance: bigint;
  isApproving: boolean;
  onApprove: (amount?: bigint) => Promise<void> | void;
  onDone?: () => void;
  demo?: boolean;
  emptyRound?: boolean;
}

export default function ReaperKey({ account, timeRemaining, allowance, isApproving, onApprove, onDone, demo, emptyRound = false }: Props) {
  const [price, setPrice] = useState<bigint>(0n);
  const [balance, setBalance] = useState<bigint | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (document.hidden) return;
    try { setPrice(await getReaperPrice()); } catch { /* keep last */ }
    if (account) {
      try { const f = await getFRAMEContract(); if (f) setBalance(BigInt(await f.balanceOf(account))); } catch { /* ignore */ }
    }
  }, [account]);

  // Price rises every second as the clock runs down; refresh every 15s (and faster near the end).
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, timeRemaining > 0 && timeRemaining < 600 ? 5_000 : 15_000);
    return () => clearInterval(t);
  }, [refresh, timeRemaining < 600]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buffer: the price keeps rising until the tx is mined.
  const buffered = price + price / 50n;
  const needsApproval = allowance < buffered;
  const lowBalance = balance !== null && price > 0n && balance < buffered;
  const reason = demo ? 'Reaper keys are not part of demo mode'
    : !account ? 'Connect a wallet to use a Reaper key'
    : emptyRound ? 'No keys sold yet: Reaper keys open after the first key'
    : timeRemaining >= 0 && timeRemaining <= SAFE_ZONE ? `Blocked in the final ${SAFE_ZONE}s (contract safe zone)`
    : price === 0n ? 'Reading the Reaper price…'
    : lowBalance ? 'Not enough test FRAME for a Reaper key'
    : null;

  const fire = async () => {
    setBusy(true);
    const id = 'reaper';
    try {
      toast.loading('Sending Reaper key…', { id });
      await buyReaperKey();
      toast.success('Reaper key landed: 30s cut from the clock.', { id });
      setConfirming(false);
      onDone?.();
      refresh();
    } catch (e: any) {
      const m = String(e?.shortMessage || e?.reason || e?.message || e);
      toast.error(/EmptyRound/i.test(m) ? 'Reaper keys open after the first key of the round.' : /RoundEnding/i.test(m) ? `Too late: Reaper keys are blocked in the final ${SAFE_ZONE}s.` : /user rejected|denied/i.test(m) ? 'Transaction cancelled.' : 'Reaper key failed. Check your test FRAME balance and allowance.', { id });
    } finally { setBusy(false); }
  };

  return (
    <div className="reaper">
      <div className="rk-head">
        <span className="rk-title">☠ Reaper key</span>
        <span className="rk-price mono">{price > 0n ? `${fmt(price)} test FRAME` : '—'}</span>
      </div>
      <div className="rk-sub">
        Cuts 30s off the timer.
        <span className="rk-tip" tabIndex={0} aria-label="Reaper key split: 55% pot, 35% dividends, 5% jackpot pool, 5% community">
          ⓘ<span role="tooltip">Split: 55% pot · 35% shared with key holders · 5% jackpot pool · 5% community. Price ≈ 5× a key and rises as the clock runs down.</span>
        </span>
      </div>

      {!confirming ? (
        <button type="button" className="rk-btn" disabled={!!reason || busy || isApproving}
          onClick={() => (needsApproval ? onApprove(buffered) : setConfirming(true))}>
          {isApproving ? 'Approving…' : needsApproval && !reason ? 'Approve FRAME for Reaper' : 'Reap 30 seconds'}
        </button>
      ) : (
        <div className="rk-confirm" role="alertdialog" aria-label="Confirm Reaper key">
          <p>Spend about <b>{fmt(price)} test FRAME</b> (≈5× a key) to cut 30s off the clock? The price can rise slightly before it's mined. It does not make you the last buyer.</p>
          <div className="rk-row">
            <button type="button" className="rk-btn" onClick={fire} disabled={busy || !!reason}>{busy ? 'Reaping…' : 'Confirm Reaper key'}</button>
            <button type="button" className="rk-cancel" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
      {reason && <div className="rk-why">{reason}</div>}
    </div>
  );
}
